/**
 * Categories: names people give nodes ("Android", "Backend"...), none at first. A graph's
 * categories are the names its nodes use, in the order they first appear; that order picks
 * each one's colour (`--category-1` … `--category-8`, then round again). The core keeps the
 * same rules (`da_types::clean_categories`, `Graph::categories`).
 */
import type { Graph } from "@protocol/Graph";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Node } from "@protocol/Node";

/** How many category colours the theme has (`--category-1` … `--category-8`). */
export const CATEGORY_COLOURS = 8;

/** The colour of nodes in no category. */
export const NO_CATEGORY_VAR = "--category-none";

/** Whether two category names are the same one (case does not count). */
export const sameCategory = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** A list of names as kept: trimmed, empty ones dropped, each once (the first spelling wins). */
export function cleanCategories(names: readonly string[]): string[] {
  const kept: string[] = [];
  for (const name of names.map((n) => n.trim()).filter(Boolean)) if (!kept.some((k) => sameCategory(k, name))) kept.push(name);
  return kept;
}

/** The categories the graph's nodes use, each once, in the order they first appear. */
export function categoriesOf(graph: Pick<Graph, "nodes">): string[] {
  return cleanCategories(graph.nodes.flatMap((n) => n.categories));
}

/** The CSS variable holding a category's colour, from its place in `order` (none: not in it). */
export function categoryVar(order: readonly string[], name: string): string {
  const at = order.findIndex((c) => sameCategory(c, name));
  return at < 0 ? NO_CATEGORY_VAR : `--category-${(at % CATEGORY_COLOURS) + 1}`;
}

/** The CSS colour of a node: its first category's, or the no-category colour. */
export function nodeCssColor(node: Pick<Node, "categories">, order: readonly string[]): string {
  const first = node.categories[0];
  return `var(${first === undefined ? NO_CATEGORY_VAR : categoryVar(order, first)})`;
}

/** How many nodes are in each category, in the graph's order. */
export function categoryCounts(graph: Pick<Graph, "nodes">): { name: string; count: number }[] {
  return categoriesOf(graph).map((name) => ({ name, count: graph.nodes.filter((n) => n.categories.some((c) => sameCategory(c, name))).length }));
}

/**
 * Whether a node in these categories is shown while the categories in `hidden` are hidden
 * (lower case; `""` stands for "no category"): shown while one of its categories is.
 */
export function shownWith(categories: readonly string[], hidden: readonly string[]): boolean {
  return categories.length === 0 ? !hidden.includes("") : categories.some((c) => !hidden.includes(c.trim().toLowerCase()));
}

/**
 * The edits (one `graph.batch`, one undo) renaming category `from` to `to` on every node in it.
 * Renaming to a category a node is already in leaves it there once.
 */
export function renameEdits(graph: Pick<Graph, "nodes">, from: string, to: string): GraphEdit[] {
  const name = to.trim();
  if (!name) return [];
  return graph.nodes.flatMap((n): GraphEdit[] => {
    if (!n.categories.some((c) => sameCategory(c, from))) return [];
    const categories = cleanCategories(n.categories.map((c) => (sameCategory(c, from) ? name : c)));
    return [{ edit: "update_node", id: n.id, patch: { categories } }];
  });
}

/** The edits (one `graph.batch`, one undo) taking every node out of category `name` (the nodes stay). */
export function removeEdits(graph: Pick<Graph, "nodes">, name: string): GraphEdit[] {
  return graph.nodes.flatMap((n): GraphEdit[] =>
    n.categories.some((c) => sameCategory(c, name)) ? [{ edit: "update_node", id: n.id, patch: { categories: n.categories.filter((c) => !sameCategory(c, name)) } }] : [],
  );
}
